import { useState } from "react";
import { format } from "date-fns";
import { HugeiconsIcon } from "@hugeicons/react";
import { PlusSignIcon } from "@hugeicons/core-free-icons";
import { Button } from "@/components/ui/button";
import { useManagerList } from "../hooks/useManagers";
import { ManagerDialog } from "./ManagerDialog";
import type { ManagerListItem } from "../types/manager.types";

interface ManagerListProps {
  companyGuid: string;
}

export function ManagerList({ companyGuid }: ManagerListProps) {
  const { data, isLoading } = useManagerList(companyGuid);
  const [dialogState, setDialogState] = useState<
    { open: false } | { open: true; mode: "add" } | { open: true; mode: "detail"; accountGuid: string }
  >({ open: false });

  const managers = data?.managers ?? [];

  const handleRowClick = (item: ManagerListItem) => {
    setDialogState({ open: true, mode: "detail", accountGuid: item.accountGuid });
  };

  const handleAdd = () => {
    setDialogState({ open: true, mode: "add" });
  };

  const handleClose = () => {
    setDialogState({ open: false });
  };

  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-12">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
        <span className="text-xs text-gray-400">담당자 목록을 불러오는 중...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between px-4 py-2">
        <span className="text-xs text-gray-500">
          총 {managers.length}명
        </span>
        <Button
          size="sm"
          onClick={handleAdd}
          className="h-7 bg-blue-600 px-2.5 text-xs font-bold hover:bg-blue-700"
        >
          <HugeiconsIcon icon={PlusSignIcon} size={12} className="mr-1" />
          담당자 추가
        </Button>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto px-4">
        {managers.length === 0 ? (
          <div className="flex h-32 items-center justify-center text-xs text-gray-400">
            등록된 담당자가 없습니다
          </div>
        ) : (
          <table className="w-full">
            <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-50">
              <tr>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">아이디</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">이름</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">부서</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">직급</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">상태</th>
                <th className="h-8 px-2 text-left text-xs font-medium text-gray-500">최근 로그인</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {managers.map((m) => (
                <tr
                  key={m.accountGuid}
                  className="h-9 cursor-pointer hover:bg-gray-50"
                  onClick={() => handleRowClick(m)}
                >
                  <td className="px-2 text-xs font-medium text-blue-600">{m.accountId}</td>
                  <td className="px-2 text-xs text-gray-700">{m.name}</td>
                  <td className="px-2 text-xs text-gray-700">{m.department ?? "-"}</td>
                  <td className="px-2 text-xs text-gray-700">{m.position ?? "-"}</td>
                  <td className="px-2">
                    {m.isActive ? (
                      <span className="inline-block rounded-full bg-green-100 px-1.5 py-0.5 text-xs text-green-700">
                        활성
                      </span>
                    ) : (
                      <span className="inline-block rounded-full bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">
                        비활성
                      </span>
                    )}
                  </td>
                  <td className="px-2 text-xs text-gray-500">
                    {m.lastLoginTime
                      ? format(new Date(m.lastLoginTime), "yyyy-MM-dd HH:mm")
                      : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Dialog */}
      <ManagerDialog
        open={dialogState.open}
        mode={dialogState.open ? dialogState.mode : "add"}
        accountGuid={dialogState.open && dialogState.mode === "detail" ? dialogState.accountGuid : null}
        companyGuid={companyGuid}
        onClose={handleClose}
      />
    </div>
  );
}
